import { HttpClient, httpResource } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { Signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  ISubscriptionPlan,
  IPaymentHistory,
  IGetPaymentManagementResponse,
  IGetPurchaseAddonUrlResponse,
  IPaginatedDateSearchParams,
} from '@dss-libs/portal/type/portal-types';
import { API_URL } from '@dss-libs/shared/utility/global-injection-tokens';
import { PAYMENT_URLS } from '../../urls/payment.urls';
import { IPaginatedResponse } from '@dss-libs/shared/type/common-types';
import { LoaderService } from '@dss-libs/shared/utility/common-services';
import { tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  readonly #apiUrl = inject(API_URL);
  readonly #http = inject(HttpClient);
  readonly #loaderService = inject(LoaderService);

  subscriptionPlansResource(shouldFetch?: Signal<boolean>) {
    return httpResource<ISubscriptionPlan[]>(
      () => (shouldFetch?.() === false ? undefined : `${this.#apiUrl}${PAYMENT_URLS.PLANS}`),
      { defaultValue: [] }
    );
  }

  paymentManagementResource(shouldFetch: Signal<boolean>) {
    return rxResource({
      params: () => (shouldFetch() ? true : undefined),
      stream: () =>
        this.#http.get<IGetPaymentManagementResponse>(`${this.#apiUrl}${PAYMENT_URLS.GET_PAYMENT_MANAGEMENT_URL}`).pipe(
          tap({
            subscribe: () => this.#loaderService.showLoader(),
            finalize: () => this.#loaderService.hideLoader(),
          })
        ),
    });
  }

  addonPurchaseUrlResource(addonPaymentId: Signal<string>, shouldFetch: Signal<boolean>) {
    return rxResource({
      params: () => {
        const paymentId = addonPaymentId();
        return shouldFetch() && paymentId ? paymentId : undefined;
      },
      stream: ({ params: paymentId }) =>
        this.#http
          .get<IGetPurchaseAddonUrlResponse>(`${this.#apiUrl}${PAYMENT_URLS.GET_ADDON_PURCHASE_URL(paymentId)}`)
          .pipe(
            tap({
              subscribe: () => this.#loaderService.showLoader(),
              finalize: () => this.#loaderService.hideLoader(),
            })
          ),
    });
  }

  paymentHistoryResource(params: Signal<IPaginatedDateSearchParams>) {
    return httpResource<IPaginatedResponse<IPaymentHistory>>(() => {
      return {
        url: `${this.#apiUrl}${PAYMENT_URLS.GET_PAYMENT_HISTORY}`,
        params: { ...params() },
      };
    });
  }
  downloadPaymentHistoryPdf(paymentId: string) {
    return this.#http.get(`${this.#apiUrl}${PAYMENT_URLS.GET_PAYMENT_HISTORY_PDF(paymentId)}`, {
      responseType: 'blob',
    });
  }
}
